import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, ImageOff } from 'lucide-react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useSocialAnalyticsMediaHistoryQuery, type SocialAnalyticsMediaHistoryQuery } from '@/generated/graphql';
import { analyticsErrorKey } from '@/lib/social-analytics/analyticsErrorKey';
import { buildHistorySeries, isolatedPointIndexes, trackingStartedAfterPublish } from '@/lib/social-analytics/buildHistorySeries';
import { computeEngagementRate } from '@/lib/social-analytics/computeEngagementRate';
import { formatAnalyticsSyncDate } from '@/lib/social-analytics/formatAnalyticsSyncDate';
import { formatMetric, formatSignedMetric } from '@/lib/social-analytics/formatMetric';
import { mediaTypeKey } from '@/lib/social-analytics/mediaTypeKey';
import { safeHttpUrl } from '@/lib/social-analytics/safeHttpUrl';
import type { MediaRow } from './AllContentTable';

type MediaHistorySheetProps = { media: MediaRow | null; onClose: () => void };
type HistoryPoints = SocialAnalyticsMediaHistoryQuery['socialAnalyticsMediaHistory'];

const EMPTY_POINTS: HistoryPoints = [];
const FALLBACK = '—';

export const MediaHistorySheet = ({ media, onClose }: MediaHistorySheetProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  const historyQuery = useSocialAnalyticsMediaHistoryQuery({ variables: { mediaId: media?.id ?? '' }, skip: !media, fetchPolicy: 'cache-and-network' });
  const points = historyQuery.data?.socialAnalyticsMediaHistory ?? EMPTY_POINTS;
  const series = useMemo(() => buildHistorySeries(points), [points]);
  const isolated = useMemo(() => isolatedPointIndexes(series), [series]);
  const errorKey = analyticsErrorKey(historyQuery.error);
  const showError = Boolean(historyQuery.error) && points.length === 0;
  const trackingSince = media ? trackingStartedAfterPublish(points, media.published_at) : null;
  const engagement = media ? computeEngagementRate(media) : null;
  const viewsLabel = t('analytics.content.columns.views');
  const formatDay = (value: string) => {
    try {
      return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
    } catch {
      return value;
    }
  };
  const metrics = media ? [
    { label: viewsLabel, value: formatMetric(media.views ?? null, locale) },
    { label: t('analytics.metrics.likes'), value: formatMetric(media.likes ?? null, locale) },
    { label: t('analytics.metrics.comments'), value: formatMetric(media.comments ?? null, locale) },
    { label: t('analytics.metrics.shares'), value: formatMetric(media.shares ?? null, locale) },
    { label: t('analytics.metrics.engagementRate'), value: engagement === null ? FALLBACK : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(engagement) },
  ] : [];
  const href = safeHttpUrl(media?.canonical_url);
  const thumbnail = safeHttpUrl(media?.thumbnail_url);

  let history: ReactNode;
  if (historyQuery.loading && points.length === 0) {
    history = <Skeleton data-testid="media-history-loading" className="h-48 w-full" />;
  } else if (showError) {
    history = (
      <div role="alert" className="flex flex-col items-start gap-2 rounded-lg border border-destructive/30 p-3">
        <p className="text-sm text-muted-foreground">{t(errorKey ?? 'analytics.drawer.error')}</p>
        {errorKey !== 'analytics.errors.mediaNotFound' && (
          <Button type="button" variant="outline" size="sm" onClick={() => { void historyQuery.refetch(); }}>{t('analytics.error.retry')}</Button>
        )}
      </div>
    );
  } else if (series.length <= 1) {
    history = <p className="text-sm text-muted-foreground">{t('analytics.drawer.singlePoint')}</p>;
  } else {
    history = (
      <div data-testid="media-history-chart">
        <ChartContainer config={{ views: { label: viewsLabel, color: 'hsl(var(--primary))' } }} className="h-[200px] min-h-[200px] w-full aspect-auto">
          <LineChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={28} tickFormatter={formatDay} />
            <YAxis tickLine={false} axisLine={false} width={44} tickFormatter={(value) => formatMetric(value, locale)} />
            <ChartTooltip
              cursor={{ stroke: 'hsl(var(--border))' }}
              content={(
                <ChartTooltipContent
                  className="text-foreground"
                  labelFormatter={(label) => formatDay(String(label))}
                  formatter={(value, _name, item) => (
                    <div className="flex flex-1 items-center justify-between gap-4 leading-none">
                      <span className="text-muted-foreground">{viewsLabel}</span>
                      <span className="font-mono font-medium tabular-nums">
                        {formatMetric(typeof value === 'number' ? value : null, locale)} ({formatSignedMetric((item.payload as { gained: number | null }).gained, locale)} {t('analytics.drawer.thatDay')})
                      </span>
                    </div>
                  )}
                />
              )}
            />
            <Line type="monotone" dataKey="views" stroke="var(--color-views)" strokeWidth={2.25} dot={(dot: { key?: string; cx?: number; cy?: number; index?: number }) => (
              dot.index !== undefined && isolated.has(dot.index) && dot.cx !== undefined && dot.cy !== undefined
                ? <circle key={dot.key ?? `dot-${dot.index}`} cx={dot.cx} cy={dot.cy} r={3} fill="var(--color-views)" />
                : <g key={dot.key ?? `dot-${dot.index}`} />
            )} connectNulls={false} activeDot={{ r: 4 }} isAnimationActive={false} />
          </LineChart>
        </ChartContainer>
      </div>
    );
  }

  return (
    <Sheet open={media !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        {media && (
          <div className="space-y-5">
            <SheetHeader className="space-y-3 text-left">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                  {thumbnail ? <img src={thumbnail} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0">
                  <SheetTitle className="truncate text-base">{media.title || media.caption || t('analytics.content.untitled')}</SheetTitle>
                  <SheetDescription>
                    @{media.account?.username || FALLBACK} · {t(`analytics.platforms.${media.platform}`)} · {t(mediaTypeKey(media.media_type))} · {formatAnalyticsSyncDate(media.published_at, locale, FALLBACK)}
                  </SheetDescription>
                </div>
              </div>
              {href && (
                <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                  {t('analytics.drawer.openOn', { platform: t(`analytics.platforms.${media.platform}`) })}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </SheetHeader>
            <dl className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {metrics.map((metric) => (
                <div key={metric.label} className="rounded-lg border border-border/60 p-2">
                  <dt className="text-[11px] text-muted-foreground">{metric.label}</dt>
                  <dd className="mt-0.5 text-sm font-semibold tabular-nums">{metric.value}</dd>
                </div>
              ))}
            </dl>
            <section aria-labelledby="media-history-title" className="space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <h3 id="media-history-title" className="text-sm font-semibold">{t('analytics.drawer.chartTitle')}</h3>
                {trackingSince && <p className="text-xs text-muted-foreground">{t('analytics.drawer.trackingSince', { date: formatDay(trackingSince) })}</p>}
              </div>
              {history}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
